import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Row, Col, Alert, Button, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useEditMode } from "../../Context/EditModeContext";
import { QuillToolbar, modules, formats } from "./EditorToolbar";
import { useTranslation } from "react-i18next"; // Importer useTranslation
import "./TheoryLesson.css";

function TheoryLesson({ lesson, setLesson }) {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const { isEditMode } = useEditMode();
  const quillRef = useRef(null);
  const [editorMounted, setEditorMounted] = useState(false);
  const [toolbarMounted, setToolbarMounted] = useState(false);
  const [key, setKey] = useState(Date.now());

  useEffect(() => {
    if (lesson && lesson.content && lesson.content !== "Non Defini") {
      setContent(lesson.content);
    } else {
      setContent("");
    }

    setEditorMounted(false);
    setToolbarMounted(false);
    setKey(Date.now());
  }, [lesson, isEditMode]);

  useEffect(() => {
    let toolbarTimer;
    let editorTimer;

    toolbarTimer = setTimeout(() => {
      setToolbarMounted(true);

      editorTimer = setTimeout(() => {
        setEditorMounted(true);
      }, 200);
    }, 100);

    return () => {
      clearTimeout(toolbarTimer);
      clearTimeout(editorTimer);
    };
  }, [key]);

  useEffect(() => {
    if (!isEditMode && content) {
      // Initialiser les challenges en mode lecture
      setTimeout(() => {
        const challenges = document.querySelectorAll(".challenge-wrapper");
        challenges.forEach((challenge) => {
          const validateBtn = challenge.querySelector(".challenge-validate");
          const checkboxes = challenge.querySelectorAll(".challenge-checkbox");
          const radios = challenge.querySelectorAll(".challenge-radio");
          const options = challenge.querySelectorAll(".challenge-option");
          const feedback = challenge.querySelector(".challenge-feedback");
          const correctAnswers = JSON.parse(
            challenge.getAttribute("data-correct") || "[]"
          );

          // Variable pour stocker le dernier radio sélectionné
          let lastSelectedRadio = null;

          // Ajouter des gestionnaires d'événements à toutes les options
          options.forEach((option) => {
            const input = option.querySelector("input");

            // Gestionnaire pour cliquer sur l'option entière
            option.addEventListener("click", function (e) {
              // Éviter de traiter deux fois si on clique directement sur l'input
              if (e.target !== input) {
                if (input.type === "checkbox") {
                  input.checked = !input.checked;
                } else if (input.type === "radio") {
                  // Pour les boutons radio, permettre la désélection en cliquant à nouveau
                  if (lastSelectedRadio === input && input.checked) {
                    // On peut simuler une désélection en désactivant tous les radios du même groupe
                    input.checked = false;
                    lastSelectedRadio = null;
                  } else {
                    input.checked = true;
                    lastSelectedRadio = input;
                  }
                }
              } else if (input.type === "radio") {
                // Si on clique directement sur le radio, mémoriser quel radio est sélectionné
                if (input.checked) {
                  lastSelectedRadio = input;
                }
              }

              // Ajouter/enlever la classe selected
              if (input.checked) {
                option.classList.add("selected");
              } else {
                option.classList.remove("selected");
              }
            });

            // Gestionnaire pour l'input lui-même (pour synchroniser l'apparence)
            input.addEventListener("change", function () {
              if (this.checked) {
                option.classList.add("selected");
                if (this.type === "radio") {
                  lastSelectedRadio = this;
                }
              } else {
                option.classList.remove("selected");
              }
            });
          });

          if (validateBtn) {
            validateBtn.addEventListener("click", function () {
              const userAnswers = [];

              checkboxes.forEach((checkbox, index) => {
                if (checkbox.checked) {
                  userAnswers.push(parseInt(checkbox.dataset.index));
                }
              });

              radios.forEach((radio, index) => {
                if (radio.checked) {
                  userAnswers.push(parseInt(radio.dataset.index));
                }
              });

              // Vérifier si les réponses sont correctes
              let isCorrect = false;

              if (correctAnswers.length > 1) {
                // Pour les challenges à choix multiples
                isCorrect =
                  userAnswers.length === correctAnswers.length &&
                  userAnswers.every((answer) =>
                    correctAnswers.includes(answer)
                  );
              } else {
                // Pour les challenges à choix unique
                isCorrect =
                  userAnswers.length === 1 &&
                  userAnswers[0] === correctAnswers[0];
              }

              // Afficher le feedback approprié
              feedback.textContent = isCorrect
                ? t("correct_answer")
                : t("incorrect_answer");

              feedback.className = isCorrect
                ? "challenge-feedback alert alert-success"
                : "challenge-feedback alert alert-danger";

              feedback.style.display = "block";

              feedback.animate(
                [
                  { opacity: 0, transform: "translateY(-10px)" },
                  { opacity: 1, transform: "translateY(0)" },
                ],
                {
                  duration: 300,
                  easing: "ease-out",
                }
              );
            });
          }
        });
      }, 500);
    }
  }, [isEditMode, content, t]);

  const handleContentChange = useCallback((value) => {
    setContent(value);
  }, []);

  const handleDownloadPDF = () => {
    if (lesson && lesson._id) {
      setIsLoading(true);
      setError("");

      const baseUrl = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
      const pdfUrl = `${baseUrl}/lessons/lesson/${lesson._id}/pdf`;
      console.log("PDF URL:", pdfUrl);

      fetch(pdfUrl, {
        method: "GET",
        credentials: "include",
      })
        .then((response) => {
          console.log("PDF response status:", response.status);
          if (!response.ok) {
            setIsLoading(false);
            throw new Error(`HTTP error ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => {
          console.log("Received blob:", blob.type, blob.size);

          if (blob.type !== "application/pdf" && blob.size < 100) {
            return blob.text().then((text) => {
              throw new Error(`Server response: ${text}`);
            });
          }

          const pdfBlob = new Blob([blob], { type: "application/pdf" });
          const url = window.URL.createObjectURL(pdfBlob);

          const newWindow = window.open();
          if (newWindow) {
            newWindow.document.write(`
              <iframe src="${url}" width="100%" height="100%" style="border: none;"></iframe>
            `);
          }

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `${lesson.title || "lesson"}.pdf`;
          document.body.appendChild(a);
          a.click();

          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setIsLoading(false);
          }, 500);
        })
        .catch((err) => {
          setIsLoading(false);
          console.error("Error downloading PDF:", err);
          setError(t("pdf_download_error") + err.message);
        });
    } else {
      setError(t("pdf_generation_error"));
    }
  };

  const handleSaveContent = async () => {
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/lessons/${lesson._id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ content }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t("generic_error"));
      }

      setSuccessMessage(t("content_saved_success"));

      if (setLesson) {
        setLesson({
          ...lesson,
          content,
        });
      }
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const customModules = useMemo(() => {
    return {
      ...modules,
      toolbar: isEditMode ? modules.toolbar : false,
    };
  }, [isEditMode]);

  return (
    <Row className="m-4">
      <Col>
        {isEditMode && toolbarMounted && (
          <div className="sticky-toolbar">
            <QuillToolbar onSave={handleSaveContent} isSaving={isLoading} />
          </div>
        )}

        {!isEditMode && content && (
          <div className="d-flex justify-content-end mb-3">
            <Button
              variant="outline-primary"
              onClick={handleDownloadPDF}
              className="download-pdf-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">{t("generating_pdf")}...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-file-pdf me-2"></i>
                  {t("download_pdf")}
                </>
              )}
            </Button>
          </div>
        )}

        {!editorMounted && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t("loading_editor")}</span>
            </Spinner>
          </div>
        )}

        {editorMounted && (toolbarMounted || !isEditMode) && (
          <ReactQuill
            ref={quillRef}
            key={`quill-${key}`}
            theme="snow"
            value={content}
            onChange={handleContentChange}
            modules={customModules}
            formats={formats}
            readOnly={!isEditMode}
            placeholder={isEditMode ? t("start_writing_lesson") : ""}
            className={`editor-main ${isEditMode ? "edit-mode" : "read-mode"}`}
          />
        )}

        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert variant="success" className="mt-3">
            {successMessage}
          </Alert>
        )}
      </Col>
    </Row>
  );
}

export default TheoryLesson;
